/**
 * Felix avatar-variant API client (OB-01) — typed wrapper over the per-org
 * avatar endpoints in app/api/v1/felix_model.py:
 *
 *   GET  /felix/avatar   — useFelixAvatar()      (query)
 *   PUT  /felix/avatar   — useSetFelixAvatar()   (mutation)
 *
 * The org picks a Felix core-shape during onboarding (OnboardingGate avatar
 * stage); the choice is persisted per-org and read back here so every
 * FelixAvatar consumer (e.g. the meeting MeetingTopBar Felix chip) renders the
 * chosen silhouette. Request/response shapes mirror the Pydantic
 * AvatarVariantResponse / AvatarVariantUpdate models EXACTLY.
 *
 * The allowed variant set lives in ONE place —
 * components/meeting/FelixAvatar.tsx FELIX_AVATAR_VARIANTS — and is imported by
 * the picker; this client only carries the wire type.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import type { FelixAvatarPref, FelixAvatarVariant } from "@/api/types";

const FELIX_AVATAR_KEY = ["felix-avatar"] as const;

// ---------------------------------------------------------------------------
// Raw API surface (also exported for tests / non-hook call sites).
// ---------------------------------------------------------------------------
export const felixAvatarApi = {
  /** GET /felix/avatar — the org's chosen avatar variant ("orb" when unset). */
  get(): Promise<FelixAvatarPref> {
    return apiClient.get<FelixAvatarPref>("/felix/avatar");
  },

  /** PUT /felix/avatar — persist the org's avatar variant. Returns the
   *  persisted variant. */
  set(variant: FelixAvatarVariant): Promise<FelixAvatarPref> {
    return apiClient.put<FelixAvatarPref>("/felix/avatar", { variant });
  },
};

// ---------------------------------------------------------------------------
// TanStack Query hooks
// ---------------------------------------------------------------------------

/** The org's current Felix avatar variant. Cached broadly (30s) since it
 *  changes rarely; consumers pass `data?.variant ?? "orb"` so the read-path is
 *  safe before the query resolves. */
export function useFelixAvatar(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: FELIX_AVATAR_KEY,
    queryFn: () => felixAvatarApi.get(),
    enabled: options?.enabled ?? true,
    staleTime: 30_000,
    retry: false,
  });
}

/**
 * Persist the org's avatar choice.
 *
 * Optimistic update + rollback: the cached variant is swapped immediately so
 * every mounted FelixAvatar re-renders with the pick with zero latency; if the
 * PUT fails the snapshot is restored so the UI never shows a variant the server
 * did not accept. On settle we invalidate to reconcile with the authoritative
 * response.
 */
export function useSetFelixAvatar() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variant: FelixAvatarVariant) => felixAvatarApi.set(variant),
    onMutate: async (variant: FelixAvatarVariant) => {
      await queryClient.cancelQueries({ queryKey: FELIX_AVATAR_KEY });
      const previous =
        queryClient.getQueryData<FelixAvatarPref>(FELIX_AVATAR_KEY);
      queryClient.setQueryData<FelixAvatarPref>(FELIX_AVATAR_KEY, { variant });
      return { previous };
    },
    onError: (_err, _variant, context) => {
      if (context?.previous) {
        queryClient.setQueryData(FELIX_AVATAR_KEY, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: FELIX_AVATAR_KEY });
    },
  });
}

export default felixAvatarApi;
